import { useEffect, useState } from 'react'

import { brand } from '@/brand/brand'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import type { DemoLockInfo } from '@/lib/api'
import { LockedStep } from './LockedStep'

/** Render the canned sample by file type (video / audio / image). */
function SampleMedia({ url }: { url?: string | null }) {
  if (!url) return null
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase()
  if (ext && ['mp4', 'webm', 'mov', 'm4v'].includes(ext)) {
    return <video src={url} controls playsInline className="w-full rounded-md" />
  }
  if (ext && ['mp3', 'wav', 'm4a', 'ogg'].includes(ext)) {
    return <audio src={url} controls className="w-full" />
  }
  if (ext && ['png', 'jpg', 'jpeg', 'webp', 'gif', 'avif'].includes(ext)) {
    return <img src={url} alt="Sample output" className="w-full rounded-md" />
  }
  return null
}

/**
 * Mounted once at the app root. Any AI/paid trigger (via apiFetch) dispatches a
 * `demo-lock` event; this opens a dialog with the branded <LockedStep> + canned sample.
 * One global surface → every AI button shows LockedStep, never a raw error.
 */
export function DemoLockOverlay() {
  const [lock, setLock] = useState<DemoLockInfo | null>(null)

  useEffect(() => {
    const onLock = (e: Event) => setLock((e as CustomEvent<DemoLockInfo>).detail || {})
    window.addEventListener('demo-lock', onLock)
    return () => window.removeEventListener('demo-lock', onLock)
  }, [])

  return (
    <Dialog open={!!lock} onOpenChange={(open) => !open && setLock(null)}>
      <DialogContent className="sm:max-w-lg">
        <DialogTitle className="sr-only">{brand.copy.lockedTitle}</DialogTitle>
        <LockedStep feature={lock?.feature} sample={<SampleMedia url={lock?.sampleUrl} />} />
      </DialogContent>
    </Dialog>
  )
}
